import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {

  Drawer,

  Box,

  Typography,

  IconButton,

  Divider,

  Button,

  CircularProgress,

  Alert,

  Grid,

  Paper,

  Tabs,

  Tab,

  Chip,

  TextField,

  Autocomplete,

  FormControl,

  Tooltip,

  Menu,

  MenuItem,

  ListItemIcon,

  ListItemText,

  Dialog,

  DialogTitle,

  DialogContent,

  DialogActions,

  Checkbox,

  Select,

  InputLabel,

} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';

import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

import UndoIcon from '@mui/icons-material/Undo';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import { DataGridPro } from '@mui/x-data-grid-pro';

import { formatAmount, formatDateOnly, formatDateTime, getFormatConfig } from '../../../utils/global';

import {
  buildExpenseDistributionFromLines,

  enrichExpenseRow,

  fetchExpenseLookupMaps,

  filterGlAccountsForBusinessUnit,

  runValidations,

  validationStatusColor,

  getLastApprovalAttempt,

  getLastApprovalFailureReason,

  buildHeaderAdditionalDataWithApprovalAttempt,

  buildFailedApprovalAttempt,

  wasTrustedVendorAutoApproved,

  TRUSTED_VENDOR_AUTO_APPROVE_COMMENT,

} from './invoiceReviewUtils';
import InvoiceLineSplitDialog from './InvoiceLineSplitDialog';
import ApprovalWorkflowPanel from './ApprovalWorkflowPanel';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateTabsSx,
  harmonateDrawerHeaderSx,
  harmonateDialogCloseIconSx,
  harmonateDialogTitleSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const VENDOR_NAME_VALIDATION_ID = 'vendorName';

const DRAWER_TAB = {
  DETAILS: 0,
  SPLIT: 1,
  REVIEW: 2,
};

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const getTenantId = () => localStorage.getItem('tenant_id') || '';
const getUserId = () => localStorage.getItem('user_id') || null;
const apiFetch = async (path, options = {}) => {
  const token = localStorage.getItem('token');
  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(options.headers || {}),
    },
  });
  if (!response.ok) {
    let message = `Request failed (${response.status})`;
    try {
      const body = await response.json();
      message = body.error || body.message || message;
    } catch { /* ignore */ }
    throw new Error(message);
  }
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? JSON.parse(text) : null;
};

const getGlAccountOptionLabel = (option) => {
  if (!option) return '';
  return option.glAccount || `${option.glCode || ''} - ${option.glDescription || ''}`;
};

const resolveGlAccountIdFromCode = (glCode, accounts) => {
  const code = String(glCode || '').trim();
  if (!code) return '';
  const account = (accounts || []).find((item) => String(item.glCode || '').trim() === code)
    || (accounts || []).find((item) => String(item.ledgerAccountId) === code);
  return account?.ledgerAccountId || '';
};

const filterGlAccountOptions = (options, { inputValue }) => {
  const query = String(inputValue || '').trim().toLowerCase();
  if (!query) return options;
  return options.filter((account) => {
    const code = String(account.glCode ?? '').trim().toLowerCase();
    const desc = String(account.glDescription ?? '').trim().toLowerCase();
    const glAccount = String(account.glAccount ?? '').trim().toLowerCase();
    return code.startsWith(query) || desc.includes(query) || glAccount.startsWith(query);
  });
};

const filterBusinessUnitOptions = (options, { inputValue }) => {
  const query = String(inputValue || '').trim().toLowerCase();
  if (!query) return options;
  return options.filter((bu) => {
    const name = String(bu.businessUnitName || bu.label || '').trim().toLowerCase();
    const code = String(bu.businessUnitCode || '').trim().toLowerCase();
    return name.includes(query) || code.startsWith(query);
  });
};

const getDepartmentOptionLabel = (option) => {
  if (!option) return '';
  return `${option.departmentCode || ''} - ${option.departmentName || ''}`;
};

const filterDepartmentOptions = (options, { inputValue }) => {
  const query = String(inputValue || '').trim().toLowerCase();
  if (!query) return options;
  return options.filter((dept) => {
    const code = String(dept.departmentCode ?? '').trim().toLowerCase();
    const name = String(dept.departmentName ?? '').trim().toLowerCase();
    return code.startsWith(query) || name.includes(query);
  });
};

const selectionIncludes = (ids, id) => (ids || []).some((item) => String(item) === String(id));

const buildLineSelectColumn = ({ selectedIds, allIds, disabled, onToggle, onToggleAll }) => ({
  field: '__select',
  headerName: '',
  width: 52,
  sortable: false,
  filterable: false,
  disableColumnMenu: true,
  align: 'center',
  headerAlign: 'center',
  renderHeader: () => {
    const allSelected = allIds.length > 0 && allIds.every((id) => selectionIncludes(selectedIds, id));
    const someSelected = selectedIds.length > 0 && !allSelected;
    return (
      <Checkbox
        size="small"
        disabled={disabled || !allIds.length}
        checked={allSelected}
        indeterminate={someSelected}
        onChange={() => onToggleAll(allSelected ? [] : allIds)}
        inputProps={{ 'aria-label': 'Select all line items' }}
      />
    );
  },
  renderCell: (params) => (
    <Checkbox
      size="small"
      disabled={disabled}
      checked={selectionIncludes(selectedIds, params.id)}
      onClick={(event) => event.stopPropagation()}
      onChange={() => onToggle(params.id)}
      inputProps={{ 'aria-label': `Select line ${params.row.lineItemNumber || params.id}` }}
    />
  ),
});

const GlBatchEditBar = ({
  disabled,
  selectedCount,
  options,
  glAccountId,
  onGlAccountIdChange,
  onApply,
}) => (
  <Paper elevation={0} sx={{ p: 1.5, mb: 1.5, border: '1px solid #e8e8e8', borderRadius: 2, bgcolor: '#fafafa' }}>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#000000' }}>
      Batch edit GL account
    </Typography>
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
      <Autocomplete
        size="small"
        disabled={disabled}
        options={options}
        getOptionLabel={getGlAccountOptionLabel}
        filterOptions={filterGlAccountOptions}
        value={options.find((account) => String(account.ledgerAccountId) === String(glAccountId)) || null}
        onChange={(_, newValue) => onGlAccountIdChange(newValue ? newValue.ledgerAccountId : '')}
        isOptionEqualToValue={(option, value) => String(option.ledgerAccountId) === String(value?.ledgerAccountId)}
        ListboxProps={{ style: { maxHeight: 280 } }}
        sx={{ minWidth: 280, flex: 1, maxWidth: 480 }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="GL Account"
            placeholder="Search GL account"
            InputLabelProps={{ shrink: true }}
          />
        )}
      />
      <Button
        variant="contained"
        disabled={disabled || !selectedCount || !glAccountId}
        onClick={() => onApply(glAccountId)}
        sx={{ textTransform: 'none', bgcolor: '#8CD867', '&:hover': { bgcolor: '#7bc45a' } }}
      >
        {selectedCount ? `Apply to ${selectedCount} selected` : 'Apply to selected'}
      </Button>
    </Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
      {selectedCount
        ? 'The selected GL account will be assigned to every checked line.'
        : 'Check the lines you want to update, choose a GL account, then Apply.'}
    </Typography>
  </Paper>
);

const ACTION_LABELS = {
  CREATED: 'Invoice Created',
  PENDING_APPROVAL: 'Submitted for Approval',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  DRAFT: 'Saved as Draft',
  COMMENT: 'Comment',
  PAID: 'Marked Paid',
  UNPAID: 'Marked Unpaid',
  PARTIAL: 'Marked Partial',
};

const PAYMENT_STATUS_OPTIONS = [
  { value: 'UNPAID', label: 'Unpaid' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'PAID', label: 'Paid' },
];

const formatPaymentStatusLabel = (value) => {
  const match = PAYMENT_STATUS_OPTIONS.find((item) => item.value === String(value || '').toUpperCase());
  return match?.label || value || 'Unpaid';
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const formatUserDisplayName = (user) => {
  if (!user) return '';
  const name = `${(user.first_name || '').trim()} ${(user.last_name || '').trim()}`.trim();
  return name || user.username || user.email || '';
};

const getCurrentUserDisplayName = () => {
  const first = (localStorage.getItem('first_name') || '').trim();
  const last = (localStorage.getItem('last_name') || '').trim();
  const fullName = `${first} ${last}`.trim();
  if (fullName) return fullName;

  const username = localStorage.getItem('username');
  if (username && !UUID_RE.test(username)) return username;

  const email = localStorage.getItem('email');
  if (email && !UUID_RE.test(email)) return email;

  return null;
};

const buildTenantUserNameMap = (users) => {
  const map = {};
  (users || []).forEach((user) => {
    if (!user?.id) return;
    const label = formatUserDisplayName(user);
    if (label) map[user.id] = label;
  });
  return map;
};

const fetchTenantUserNameMap = async (apiFetchFn, tenantId) => {
  if (!tenantId || !apiFetchFn) return {};
  try {
    const users = await apiFetchFn(`conductor/tenant/${tenantId}/all-users`);
    return buildTenantUserNameMap(Array.isArray(users) ? users : []);
  } catch {
    return {};
  }
};

const getAuditActorLabel = (entry, userNameMap = {}) => {
  const storedName = entry?.userName?.trim();
  if (storedName && !UUID_RE.test(storedName)) return storedName;

  const userId = entry?.userId;
  if (userId && userNameMap[userId]) return userNameMap[userId];

  if (userId && userId === localStorage.getItem('user_id')) {
    const current = getCurrentUserDisplayName();
    if (current) return current;
  }

  return userId ? 'Unknown user' : 'System';
};

const parseHeaderAdditionalData = (header) => {
  try {
    const raw = header?.headerAdditionalData;
    if (!raw) return {};
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return {};
  }
};

const loadInvoiceSourcePdf = async (header, tenantId) => {
  const meta = parseHeaderAdditionalData(header);
  let fileInstanceId = meta.fileInstanceId || meta.sourceFileInstanceId || null;

  if (!fileInstanceId && meta.unitOfWorkId && tenantId) {
    try {
      const fileInstance = await apiFetch(
        `conductor/file-instances/by-unit-of-work?unit_of_work_id=${encodeURIComponent(meta.unitOfWorkId)}&tenant_id=${encodeURIComponent(tenantId)}`
      );
      fileInstanceId = fileInstance?.id || null;
    } catch {
      return { url: null, fileInstanceId: null };
    }
  }

  if (!fileInstanceId) {
    return { url: null, fileInstanceId: null };
  }

  const presigned = await apiFetch(`conductor/file-instances/${fileInstanceId}/presigned-url`);
  return {
    url: presigned?.url || null,
    fileInstanceId,
  };
};

const getWorkflowActionLabel = (status) => ACTION_LABELS[status] || status || 'Updated';

const appendWorkflowAuditEntry = (header, { nextStatus, comment, userId, userName, actionOverride }) => {
  const meta = parseHeaderAdditionalData(header);
  const history = Array.isArray(meta.workflowHistory) ? [...meta.workflowHistory] : [];

  history.push({
    id: `${Date.now()}-${history.length}`,
    action: actionOverride || getWorkflowActionLabel(nextStatus),
    status: nextStatus,
    comment: comment?.trim() || null,
    userId: userId || null,
    userName: userName || null,
    timestamp: new Date().toISOString(),
  });

  return JSON.stringify({ ...meta, workflowHistory: history });
};

const getRevertWorkflowStatus = (header) => {
  const meta = parseHeaderAdditionalData(header);
  const history = Array.isArray(meta.workflowHistory) ? meta.workflowHistory : [];
  if (!history.length) return 'PENDING_APPROVAL';

  const chronological = [...history].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  for (let i = chronological.length - 1; i >= 0; i -= 1) {
    if (chronological[i].status === 'APPROVED' && i > 0) {
      const previous = (chronological[i - 1].status || '').toUpperCase();
      if (previous && previous !== 'APPROVED') return previous;
    }
  }

  return 'PENDING_APPROVAL';
};

const invoiceLinkedToPaymentBatch = async (tenantId, apInvoiceId) => {
  const mappings = await apiFetch(
    `conductor/invoice-payment-mappings/by-ap-invoice?tenant_id=${tenantId}&ap_invoice_id=${apInvoiceId}`,
  ).catch(() => []);

  const paymentIds = (Array.isArray(mappings) ? mappings : [])
    .map((mapping) => mapping.paymentId)
    .filter(Boolean);

  if (!paymentIds.length) return false;

  const transactions = await Promise.all(
    paymentIds.map((paymentId) => apiFetch(`conductor/payment-transactions/${paymentId}`).catch(() => null)),
  );

  return transactions.some((transaction) => Boolean(transaction?.paymentBatchId));
};

const buildInvoiceAuditTrail = (header) => {
  if (!header) return [];

  const meta = parseHeaderAdditionalData(header);
  const stored = Array.isArray(meta.workflowHistory) ? meta.workflowHistory : [];

  if (stored.length > 0) {
    return [...stored].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  }

  const fallback = [];
  if (header.createdAt) {
    fallback.push({
      id: 'created',
      action: 'Invoice Created',
      status: 'DRAFT',
      comment: null,
      userId: header.createdBy || null,
      userName: null,
      timestamp: header.createdAt,
    });
  }

  const workflowStatus = (header.workflowStatus || '').toUpperCase();
  if (workflowStatus && workflowStatus !== 'DRAFT' && header.updatedAt) {
    fallback.push({
      id: 'current-status',
      action: getWorkflowActionLabel(workflowStatus),
      status: workflowStatus,
      comment: null,
      userId: header.updatedBy || null,
      userName: null,
      timestamp: header.updatedAt,
    });
  }

  return fallback.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};

const InfoField = ({ label, value }) => (

  <Box sx={{ mb: 1.5 }}>

    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>

      {label}

    </Typography>

    <Typography variant="body2" sx={{ fontWeight: 500 }}>

      {value ?? '—'}

    </Typography>

  </Box>

);



const InvoicePreviewDrawer = ({

  open,

  onClose,

  invoiceId,

  draftData,

  tenantId,

  onConfirmed,

  initialTab = 0,

  showSuccess,

  showError,

}) => {
  const { canAdd, canEdit } = useFunctionPermissions(FC.PAYMENT_INTELLIGENCE);

  const [loading, setLoading] = useState(false);

  const [confirming, setConfirming] = useState(false);

  const [header, setHeader] = useState(null);

  const [lines, setLines] = useState([]);

  const [expenseLookups, setExpenseLookups] = useState({});

  const [error, setError] = useState(null);

  const [drawerTab, setDrawerTab] = useState(0);

  const [comment, setComment] = useState('');

  const [userNameMap, setUserNameMap] = useState({});

  const [glAccounts, setGlAccounts] = useState([]);

  const [departments, setDepartments] = useState([]);

  const [vendors, setVendors] = useState([]);

  const [lineSplits, setLineSplits] = useState([]);

  const [splitDialogOpen, setSplitDialogOpen] = useState(false);

  const [splitDialogLine, setSplitDialogLine] = useState(null);

  const [splitDialogMode, setSplitDialogMode] = useState('add');

  const [lineMenuAnchor, setLineMenuAnchor] = useState(null);

  const [lineMenuRow, setLineMenuRow] = useState(null);

  const [sourcePdfUrl, setSourcePdfUrl] = useState(null);

  const [sourcePdfLoading, setSourcePdfLoading] = useState(false);

  const [sourcePdfError, setSourcePdfError] = useState(null);

  const [hasPaymentBatch, setHasPaymentBatch] = useState(false);

  const [checkingPaymentBatch, setCheckingPaymentBatch] = useState(false);

  const [documentFullscreen, setDocumentFullscreen] = useState(false);
  const [isDrawerFullscreen, setIsDrawerFullscreen] = useState(false);
  const [selectedLineIds, setSelectedLineIds] = useState([]);
  const [selectedSplitLineIds, setSelectedSplitLineIds] = useState([]);
  const [batchGlAccountId, setBatchGlAccountId] = useState('');
  const [mappingMemoryByLineId, setMappingMemoryByLineId] = useState({});
  const [approvalProgress, setApprovalProgress] = useState(null);
  const [approvalWorkflowConfigured, setApprovalWorkflowConfigured] = useState(false);
  const [paymentOverrideOpen, setPaymentOverrideOpen] = useState(false);
  const [paymentOverrideStatus, setPaymentOverrideStatus] = useState('PAID');
  const [paymentOverrideReason, setPaymentOverrideReason] = useState('');
  const [paymentOverrideSaving, setPaymentOverrideSaving] = useState(false);

  const isDraftPreview = Boolean(draftData && !invoiceId);

  const config = getFormatConfig();



  const fetchInvoiceDetails = useCallback(async () => {

    if (!invoiceId || !tenantId) return;

    try {

      setLoading(true);

      setError(null);



      const [headerData, linesData, lookups, glAccounts, departments, vendorList, splitsData] = await Promise.all([

        apiFetch(`conductor/ap-invoice-headers/${invoiceId}`),

        apiFetch(`conductor/ap-invoice-lines/by-ap-invoice?tenant_id=${tenantId}&ap_invoice_id=${invoiceId}`),

        fetchExpenseLookupMaps(tenantId),

        apiFetch(`conductor/cash-recon/chart-of-account/by-tenant?tenant_id=${tenantId}`).catch(() => []),

        apiFetch(`conductor/department-masters/tenant/${tenantId}`).catch(() => []),

        apiFetch(`conductor/vendor-masters/tenant/${tenantId}`).catch(() => []),

        apiFetch(`conductor/ap-invoice-line-splits/by-ap-invoice?tenant_id=${tenantId}&ap_invoice_id=${invoiceId}`).catch(() => []),

      ]);



      setHeader(headerData);

      let nextLines = Array.isArray(linesData) ? linesData : [];
      let mappingByLine = {};
      let resolvedHeader = headerData;
      const workflowStatus = String(headerData?.workflowStatus || 'DRAFT').toUpperCase();
      if (workflowStatus !== 'APPROVED') {
        try {
          const applyResult = await apiFetch('conductor/ap-invoice-line-mapping-memory/apply-to-invoice', {
            method: 'POST',
            body: JSON.stringify({
              tenantId,
              apInvoiceId: invoiceId,
              updatedBy: getUserId(),
            }),
          });
          const suggestions = Array.isArray(applyResult?.suggestions) ? applyResult.suggestions : [];
          suggestions.forEach((item) => {
            if (item?.apInvoiceLineId) {
              mappingByLine[String(item.apInvoiceLineId)] = item;
            }
          });
          const appliedIds = (applyResult?.appliedLineIds || []).map((id) => String(id));
          const [refreshedHeader, freshLines] = await Promise.all([
            apiFetch(`conductor/ap-invoice-headers/${invoiceId}`).catch(() => null),
            appliedIds.length
              ? apiFetch(`conductor/ap-invoice-lines/by-ap-invoice?tenant_id=${tenantId}&ap_invoice_id=${invoiceId}`).catch(() => null)
              : Promise.resolve(null),
          ]);
          if (refreshedHeader) {
            resolvedHeader = refreshedHeader;
          }
          if (appliedIds.length) {
            if (Array.isArray(freshLines)) {
              nextLines = freshLines;
            } else {
              const appliedSet = new Set(appliedIds);
              nextLines = nextLines.map((line) => {
                const suggestion = mappingByLine[String(line.apInvoiceLineId)];
                if (!suggestion?.applied || !appliedSet.has(String(line.apInvoiceLineId))) return line;
                return {
                  ...line,
                  glAccountId: suggestion.glAccountId || line.glAccountId,
                  businessUnitId: suggestion.businessUnitId || line.businessUnitId,
                };
              });
            }
          }
        } catch {
          mappingByLine = {};
        }
      }

      setHeader(resolvedHeader);

      const openedAsDraft = workflowStatus !== 'APPROVED';
      const nowApproved = String(resolvedHeader?.workflowStatus || 'DRAFT').toUpperCase() === 'APPROVED';
      if (openedAsDraft && nowApproved && wasTrustedVendorAutoApproved(resolvedHeader)) {
        showSuccess?.(
          `${TRUSTED_VENDOR_AUTO_APPROVE_COMMENT}. Payment Proposal will pick it up.`,
          'Auto-approved',
        );
        setDrawerTab(DRAWER_TAB.REVIEW);
        onConfirmed?.();
      }

      setLines(nextLines);
      setMappingMemoryByLineId(mappingByLine);

      setExpenseLookups(lookups);

      setGlAccounts(Array.isArray(glAccounts) ? glAccounts : []);

      setDepartments(Array.isArray(departments) ? departments : []);

      setVendors(Array.isArray(vendorList) ? vendorList : []);

      setLineSplits(Array.isArray(splitsData) ? splitsData : []);

      try {
        const [activeWorkflows, progressData] = await Promise.all([
          apiFetch(`conductor/approval-workflows/tenant/${tenantId}/by-type?workflow_type=AP_INVOICE`).catch(() => []),
          apiFetch(
            `conductor/approval-workflows/progress?tenant_id=${tenantId}&transaction_type=AP_INVOICE&transaction_id=${invoiceId}&current_user_id=${encodeURIComponent(localStorage.getItem('user_id') || '')}`,
          ).catch(() => null),
        ]);
        setApprovalWorkflowConfigured(
          (Array.isArray(activeWorkflows) ? activeWorkflows : []).some((wf) => {
            if (!wf?.propertyId) return true;
            return headerData?.businessUnitId
              && String(wf.propertyId) === String(headerData.businessUnitId);
          }),
        );
        const progressStatus = String(progressData?.workflowStatus || '').toUpperCase();
        setApprovalProgress(
          progressData?.approvalWorkflowProgressId && progressStatus !== 'CANCELLED'
            ? progressData
            : null,
        );
      } catch {
        setApprovalWorkflowConfigured(false);
        setApprovalProgress(null);
      }

      setCheckingPaymentBatch(true);
      try {
        const inBatch = await invoiceLinkedToPaymentBatch(tenantId, invoiceId);
        setHasPaymentBatch(inBatch);
      } catch {
        setHasPaymentBatch(false);
      } finally {
        setCheckingPaymentBatch(false);
      }

    } catch (err) {

      setError(err.message || 'Failed to load invoice preview');

      showError?.(err.message || 'Failed to load invoice preview');

    } finally {

      setLoading(false);

    }

  }, [invoiceId, tenantId, showError, showSuccess, onConfirmed]);

  const notifyTrustedVendorAutoApproved = useCallback((refreshed, previousStatus) => {
    const nextStatus = String(refreshed?.workflowStatus || 'DRAFT').toUpperCase();
    const prev = String(previousStatus || 'DRAFT').toUpperCase();
    if (nextStatus !== 'APPROVED' || prev === 'APPROVED' || !wasTrustedVendorAutoApproved(refreshed)) {
      return false;
    }
    showSuccess?.(
      `${TRUSTED_VENDOR_AUTO_APPROVE_COMMENT}. Payment Proposal will pick it up.`,
      'Auto-approved',
    );
    setDrawerTab(DRAWER_TAB.REVIEW);
    onConfirmed?.();
    return true;
  }, [showSuccess, onConfirmed]);

  const refreshHeaderAfterCodingSave = useCallback(async (previousStatus) => {
    if (!invoiceId || isDraftPreview) return null;
    try {
      const refreshed = await apiFetch(`conductor/ap-invoice-headers/${invoiceId}`);
      if (!refreshed) return null;
      setHeader(refreshed);
      notifyTrustedVendorAutoApproved(refreshed, previousStatus);
      return refreshed;
    } catch {
      return null;
    }
  }, [invoiceId, isDraftPreview, notifyTrustedVendorAutoApproved]);



  useEffect(() => {

    if (!open) {

      setDrawerTab(0);

      setComment('');

      setDocumentFullscreen(false);
      setIsDrawerFullscreen(false);
      setSelectedLineIds([]);
      setSelectedSplitLineIds([]);
      setBatchGlAccountId('');
      setMappingMemoryByLineId({});
      setPaymentOverrideOpen(false);
      setPaymentOverrideReason('');
      setPaymentOverrideSaving(false);

      return;

    }

    setDrawerTab(initialTab);

    if (isDraftPreview) {

      setHeader(draftData.header || {});

      setLines(draftData.lines || []);

      setError(null);

      setLoading(false);

      setHasPaymentBatch(false);

      setCheckingPaymentBatch(false);

      setApprovalProgress(null);

      setApprovalWorkflowConfigured(false);

      if (tenantId) {
        apiFetch(`conductor/vendor-masters/tenant/${tenantId}`)
          .then((vendorList) => setVendors(Array.isArray(vendorList) ? vendorList : []))
          .catch(() => setVendors([]));
        apiFetch(`conductor/department-masters/tenant/${tenantId}`)
          .then((deptList) => setDepartments(Array.isArray(deptList) ? deptList : []))
          .catch(() => setDepartments([]));
      }

      return;

    }

    fetchInvoiceDetails();

  }, [open, invoiceId, draftData, tenantId, fetchInvoiceDetails]);



  useEffect(() => {

    if (!open || !tenantId) return;

    let cancelled = false;

    fetchTenantUserNameMap(apiFetch, tenantId).then((map) => {

      if (!cancelled) setUserNameMap(map);

    });

    return () => {

      cancelled = true;

    };

  }, [open, tenantId]);



  const sourceDocumentLookupKey = useMemo(() => {
    if (!open || isDraftPreview || !tenantId || (!header && !invoiceId)) return null;
    const meta = parseHeaderAdditionalData(header);
    return [
      invoiceId || header?.apInvoiceId || '',
      meta.unitOfWorkId || '',
      meta.fileInstanceId || meta.sourceFileInstanceId || '',
      meta.sourceDocumentIntegrationId != null ? String(meta.sourceDocumentIntegrationId) : '',
    ].join('|');
  }, [
    open,
    isDraftPreview,
    tenantId,
    invoiceId,
    header?.apInvoiceId,
    header?.headerAdditionalData,
  ]);

  // Keep latest header for PDF lookup without reloading when department/GL fields change.
  const headerRef = useRef(header);
  headerRef.current = header;

  const linesRef = useRef(lines);
  linesRef.current = lines;

  useEffect(() => {
    if (!sourceDocumentLookupKey) {
      setSourcePdfUrl(null);
      setSourcePdfError(null);
      setSourcePdfLoading(false);
      return undefined;
    }

    let cancelled = false;

    const loadPdf = async () => {
      setSourcePdfLoading(true);
      setSourcePdfError(null);
      setSourcePdfUrl(null);

      try {
        const result = await loadInvoiceSourcePdf(headerRef.current, tenantId);
        if (cancelled) return;
        setSourcePdfUrl(result.url);
      } catch (err) {
        if (!cancelled) {
          setSourcePdfError(err.message || 'Failed to load invoice preview');
        }
      } finally {
        if (!cancelled) setSourcePdfLoading(false);
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
    };
  }, [sourceDocumentLookupKey, tenantId]);



  const splitsByLineId = useMemo(() => {
    const map = {};
    lineSplits.forEach((split) => {
      const key = split.apInvoiceLineId;
      if (!map[key]) map[key] = [];
      map[key].push(split);
    });
    return map;
  }, [lineSplits]);

  const businessUnitOptions = useMemo(
    () => {
      const list = expenseLookups.businessUnitList;
      if (Array.isArray(list) && list.length) {
        return list.map((u) => ({
          businessUnitId: u.businessUnitId,
          businessUnitName: u.businessUnitName,
          propertyId: u.propertyId,
        }));
      }
      return Object.entries(expenseLookups.businessUnits || {}).map(([businessUnitId, businessUnitName]) => ({
        businessUnitId,
        businessUnitName,
      }));
    },
    [expenseLookups.businessUnits, expenseLookups.businessUnitList]
  );

  const openSplitDialog = useCallback((row, mode = 'add') => {
    const status = (header?.workflowStatus || 'DRAFT').toUpperCase();
    if (mode === 'add' && status === 'APPROVED') return;
    if (mode === 'add') {
      const needsAdd = !(row?.isSplit);
      if (needsAdd && !canAdd) return;
      if (!needsAdd && !canEdit) return;
    }

    const lineId = row.apInvoiceLineId || row.id;
    const sourceLine = lines.find((line) => line.apInvoiceLineId === lineId);
    setSplitDialogLine({
      apInvoiceLineId: lineId,
      description: row.description,
      businessUnitId: sourceLine?.businessUnitId || row.businessUnitId,
      glAccountId: sourceLine?.glAccountId || row.glAccountId,
      amount: Number(sourceLine?.lineItemTotal) || Number(row.amount) || 0,
    });
    setSplitDialogMode(mode);
    setSplitDialogOpen(true);
  }, [lines, header, canAdd, canEdit]);

  const closeLineMenu = () => {
    setLineMenuAnchor(null);
    setLineMenuRow(null);
  };

  const expenseDistribution = useMemo(

    () => buildExpenseDistributionFromLines(lines).map((row) => ({

      ...row,

      apInvoiceLineId: row.id,

      businessUnitId: row.businessUnitId || header?.businessUnitId || null,

      isSplit: Boolean(row.isSplit) || Boolean((splitsByLineId[row.id] || []).length),

    })),

    [lines, header, splitsByLineId]

  );



  const expenseLines = useMemo(

    () => expenseDistribution.map((row) => enrichExpenseRow(row, expenseLookups)),

    [expenseDistribution, expenseLookups]

  );



  const validations = useMemo(() => {

    if (!header || isDraftPreview) return [];

    return runValidations(header, expenseDistribution, vendors);

  }, [header, expenseDistribution, isDraftPreview, vendors]);



  const hasBlockingValidationFailures = validations.some((v) => v.status === 'FAIL');

  const hasBlockingVendorValidations = validations.some(
    (v) => v.id === VENDOR_NAME_VALIDATION_ID && (v.status === 'WARN' || v.status === 'FAIL')
  );

  const hasMissingGlAccount = useMemo(() => {
    if (isDraftPreview || !lines.length) return false;
    return lines.some((line) => {
      const splits = splitsByLineId[line.apInvoiceLineId] || [];
      if (splits.length) {
        return splits.some((split) => !split.glAccountId);
      }
      return !line.glAccountId;
    });
  }, [lines, isDraftPreview, splitsByLineId]);

  const approveBlockReason = useMemo(() => {
    const status = (header?.workflowStatus || 'DRAFT').toUpperCase();
    if (status === 'APPROVED' || status === 'REJECTED') return '';
    if (hasMissingGlAccount) return 'Assign a GL account before approving.';
    if (hasBlockingVendorValidations) {
      const issues = validations
        .filter((v) => v.id === VENDOR_NAME_VALIDATION_ID && (v.status === 'WARN' || v.status === 'FAIL'))
        .map((v) => v.label)
        .join(', ');
      return `Resolve vendor name validation before approving${issues ? `: ${issues}` : ''}.`;
    }
    if (hasBlockingValidationFailures) {
      return `Resolve validation failures: ${
        validations.filter((v) => v.status === 'FAIL').map((v) => v.label).join(', ')
      }`;
    }
    return '';
  }, [
    header,
    hasMissingGlAccount,
    hasBlockingVendorValidations,
    hasBlockingValidationFailures,
    validations,
  ]);

  const canApprove = !approveBlockReason;

  const trustedVendorAutoApproved = useMemo(
    () => !isDraftPreview && wasTrustedVendorAutoApproved(header),
    [header, isDraftPreview],
  );

  const lastApprovalAttempt = useMemo(
    () => (isDraftPreview ? null : getLastApprovalAttempt(header)),
    [header, isDraftPreview]
  );

  const approvalFailureAlert = useMemo(() => {
    if (isDraftPreview) return '';
    const status = (header?.workflowStatus || 'DRAFT').toUpperCase();
    if (status === 'APPROVED') return '';
    return approveBlockReason || lastApprovalAttempt?.reason || '';
  }, [isDraftPreview, header?.workflowStatus, approveBlockReason, lastApprovalAttempt]);

  const persistApprovalFailure = useCallback(async (reason, failedChecks = null) => {
    if (!invoiceId || !header || isDraftPreview) return;
    const userId = localStorage.getItem('user_id') || null;
    const attempt = buildFailedApprovalAttempt(reason, { userId, failedChecks });
    const headerAdditionalData = buildHeaderAdditionalDataWithApprovalAttempt(header, attempt);
    const payload = {
      ...header,
      headerAdditionalData,
      updatedBy: userId,
    };
    try {
      const updated = await apiFetch(`conductor/ap-invoice-headers/${invoiceId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setHeader(updated || { ...header, headerAdditionalData });
    } catch (err) {
      // Keep the on-screen alert even if persistence fails.
      setHeader((prev) => (prev
        ? { ...prev, headerAdditionalData }
        : prev));
      console.warn('Failed to persist approval failure:', err?.message || err);
    }
  }, [invoiceId, header, isDraftPreview]);

  const clearPersistedApprovalFailure = useCallback(async () => {
    if (!invoiceId || !header || isDraftPreview) return;
    if (!getLastApprovalFailureReason(header)) return;
    const userId = localStorage.getItem('user_id') || null;
    const headerAdditionalData = buildHeaderAdditionalDataWithApprovalAttempt(header, null);
    const payload = {
      ...header,
      headerAdditionalData,
      updatedBy: userId,
    };
    try {
      const updated = await apiFetch(`conductor/ap-invoice-headers/${invoiceId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      setHeader(updated || { ...header, headerAdditionalData });
    } catch (err) {
      console.warn('Failed to clear approval failure:', err?.message || err);
    }
  }, [invoiceId, header, isDraftPreview]);

  const syncedApprovalFailureRef = useRef('');

  useEffect(() => {
    syncedApprovalFailureRef.current = '';
  }, [invoiceId, open]);

  // Keep a durable approval-failure marker in sync for Dashboard row alerts.
  useEffect(() => {
    if (!open || !header || isDraftPreview || loading) return;
    const status = (header.workflowStatus || 'DRAFT').toUpperCase();
    if (status === 'APPROVED' || status === 'REJECTED') return;

    const existingReason = getLastApprovalFailureReason(header);
    if (approveBlockReason) {
      if (syncedApprovalFailureRef.current === approveBlockReason) return;
      if (existingReason === approveBlockReason) {
        syncedApprovalFailureRef.current = approveBlockReason;
        return;
      }
      syncedApprovalFailureRef.current = approveBlockReason;
      persistApprovalFailure(approveBlockReason);
      return;
    }
    if (existingReason) {
      syncedApprovalFailureRef.current = '';
      clearPersistedApprovalFailure();
    }
  }, [
    open,
    header,
    isDraftPreview,
    loading,
    approveBlockReason,
    persistApprovalFailure,
    clearPersistedApprovalFailure,
  ]);

  const auditTrail = useMemo(
    () => buildInvoiceAuditTrail(header),
    [header]
  );

  const sharedGlAccountId = useMemo(() => {
    if (!lines.length) return '';
    const ids = [...new Set(lines.map((line) => line.glAccountId || ''))];
    if (ids.length === 1) return ids[0];
    return '';
  }, [lines]);

  const headerGlAccountId = useMemo(
    () => resolveGlAccountIdFromCode(header?.glCode, glAccounts),
    [header?.glCode, glAccounts]
  );

  const propertyGlAccounts = useMemo(
    () => filterGlAccountsForBusinessUnit(
      glAccounts,
      businessUnitOptions,
      header?.businessUnitId,
      headerGlAccountId || sharedGlAccountId
    ),
    [glAccounts, businessUnitOptions, header?.businessUnitId, headerGlAccountId, sharedGlAccountId]
  );

  const sharedDepartmentId = useMemo(() => header?.departmentId || '', [header?.departmentId]);

  const selectedDepartment = useMemo(() => {
    if (!sharedDepartmentId) return null;
    return departments.find((item) => String(item.departmentId) === String(sharedDepartmentId)) || null;
  }, [departments, sharedDepartmentId]);

  const activeDepartments = useMemo(
    () => departments.filter((dept) => !dept.status || String(dept.status).toUpperCase() === 'ACTIVE'),
    [departments]
  );

  const getBusinessUnitLabel = useCallback((businessUnitId, fallback = '') => {
    if (!businessUnitId) return fallback || '—';
    const match = businessUnitOptions.find((item) => String(item.businessUnitId) === String(businessUnitId));
    return match?.businessUnitName || expenseLookups.businessUnits?.[businessUnitId] || fallback || '—';
  }, [businessUnitOptions, expenseLookups]);

  const getGlAccountLabel = useCallback((glAccountId) => {
    if (!glAccountId) return '—';
    const account = glAccounts.find((item) => String(item.ledgerAccountId) === String(glAccountId));
    if (account) return getGlAccountOptionLabel(account);
    return expenseLookups.glAccounts?.[glAccountId] || glAccountId;
  }, [glAccounts, expenseLookups]);

  const handleApplyGlAccountToLines = useCallback(async (glAccountId, lineIds = null) => {
    const status = (header?.workflowStatus || 'DRAFT').toUpperCase();
    if (status === 'APPROVED') return;

    const value = glAccountId || null;
    const userId = localStorage.getItem('user_id') || null;
    const targetIds = Array.isArray(lineIds) && lineIds.length
      ? new Set(lineIds.map((id) => String(id)))
      : null;
    let updatedLines = [];

    setLines((prev) => {
      updatedLines = prev.map((line) => {
        const lineId = String(line.apInvoiceLineId || '');
        if (targetIds && !targetIds.has(lineId)) return line;
        return { ...line, glAccountId: value };
      });
      return updatedLines;
    });

    if (isDraftPreview || !invoiceId || !header) return;

    const previousStatus = header?.workflowStatus;

    const persistableLines = updatedLines.filter((line) => {
      if (!line.apInvoiceLineId) return false;
      if (!targetIds) return true;
      return targetIds.has(String(line.apInvoiceLineId));
    });

    const splitUpdates = persistableLines
      .map((line) => {
        const existing = splitsByLineId[line.apInvoiceLineId] || [];
        if (!existing.length) return null;
        return apiFetch('conductor/ap-invoice-line-splits/by-ap-invoice-line', {
          method: 'PUT',
          body: JSON.stringify({
            tenantId,
            apInvoiceId: invoiceId,
            apInvoiceLineId: line.apInvoiceLineId,
            updatedBy: userId,
            splits: existing.map((split) => ({
              businessUnitId: split.businessUnitId,
              glAccountId: value,
              splitAmount: Number(split.splitAmount),
            })),
          }),
        });
      })
      .filter(Boolean);

    try {
      const results = await Promise.all([
        ...persistableLines.map((line) => apiFetch(`conductor/ap-invoice-lines/${line.apInvoiceLineId}`, {
          method: 'PUT',
          body: JSON.stringify({
            ...line,
            glAccountId: value,
            updatedBy: userId,
          }),
        })),
        ...splitUpdates,
      ]);
      const savedLines = results.slice(0, persistableLines.length);
      const savedSplits = results.slice(persistableLines.length);

      if (savedLines.length) {
        const byId = Object.fromEntries(
          savedLines.filter(Boolean).map((line) => [String(line.apInvoiceLineId), line])
        );
        setLines((prev) => prev.map((line) => byId[String(line.apInvoiceLineId)] || line));
        const changedIds = new Set(persistableLines.map((line) => String(line.apInvoiceLineId)));
        setMappingMemoryByLineId((prev) => {
          const next = { ...prev };
          let changed = false;
          changedIds.forEach((id) => {
            if (next[id]) {
              delete next[id];
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      }
      if (savedSplits.length) {
        const nextSplits = savedSplits.flatMap((item) => (Array.isArray(item) ? item : []));
        if (nextSplits.length) {
          const updatedLineIds = new Set(persistableLines.map((line) => line.apInvoiceLineId));
          setLineSplits((prev) => [
            ...prev.filter((split) => !updatedLineIds.has(split.apInvoiceLineId)),
            ...nextSplits,
          ]);
        }
      }
      await refreshHeaderAfterCodingSave(previousStatus);
      const count = persistableLines.length;
      showSuccess?.(
        count === 1
          ? 'GL account applied to 1 line.'
          : `GL account applied to ${count} lines.`
      );
    } catch (err) {
      showError?.(err.message || 'Failed to save GL account');
      fetchInvoiceDetails();
    }
  }, [
    isDraftPreview,
    invoiceId,
    header,
    splitsByLineId,
    tenantId,
    showError,
    showSuccess,
    fetchInvoiceDetails,
    refreshHeaderAfterCodingSave,
  ]);

  const toggleLineSelection = useCallback((id) => {
    setSelectedLineIds((prev) => (
      selectionIncludes(prev, id)
        ? prev.filter((item) => String(item) !== String(id))
        : [...prev, id]
    ));
  }, []);

  const toggleSplitLineSelection = useCallback((id) => {
    setSelectedSplitLineIds((prev) => (
      selectionIncludes(prev, id)
        ? prev.filter((item) => String(item) !== String(id))
        : [...prev, id]
    ));
  }, []);

  const allLineIds = useMemo(
    () => lines.map((line, index) => line.apInvoiceLineId || `draft-line-${index}`),
    [lines]
  );

  const allExpenseLineIds = useMemo(
    () => expenseLines.map((row) => row.id || row.apInvoiceLineId).filter(Boolean),
    [expenseLines]
  );

  const applyBatchGlToDetails = useCallback((glAccountId) => {
    if (!selectedLineIds.length || !glAccountId) return;
    handleApplyGlAccountToLines(glAccountId, selectedLineIds);
  }, [handleApplyGlAccountToLines, selectedLineIds]);

  const applyBatchGlToSplit = useCallback((glAccountId) => {
    if (!selectedSplitLineIds.length || !glAccountId) return;
    handleApplyGlAccountToLines(glAccountId, selectedSplitLineIds);
  }, [handleApplyGlAccountToLines, selectedSplitLineIds]);

  const handleDepartmentChange = useCallback(async (departmentId) => {
    const status = (header?.workflowStatus || 'DRAFT').toUpperCase();
    if (status === 'APPROVED') return;

    const value = departmentId || null;
    const userId = localStorage.getItem('user_id') || null;

    setHeader((prev) => (prev ? { ...prev, departmentId: value } : prev));

    if (isDraftPreview || !invoiceId || !header) return;

    const headerPayload = { ...header, departmentId: value, updatedBy: userId };

    try {
      await apiFetch(`conductor/ap-invoice-headers/${invoiceId}`, {
        method: 'PUT',
        body: JSON.stringify(headerPayload),
      });
    } catch (err) {
      showError?.(err.message || 'Failed to save department');
      fetchInvoiceDetails();
    }
  }, [isDraftPreview, invoiceId, header, showError, fetchInvoiceDetails]);

  const appliedMappingCount = useMemo(
    () => Object.values(mappingMemoryByLineId).filter((item) => item?.applied).length,
    [mappingMemoryByLineId]
  );

  const canEditLineFields = useMemo(() => {
    if (isDraftPreview || !canEdit) return false;
    const status = (header?.workflowStatus || 'DRAFT').toUpperCase();
    return status !== 'APPROVED';
  }, [isDraftPreview, header?.workflowStatus, canEdit]);

  const toLineNumber = useCallback((value) => {
    if (value === '' || value == null) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  const processLineRowUpdate = useCallback(async (newRow, oldRow) => {
    if (!canEditLineFields) return oldRow;

    const lineId = newRow.id;
    const existingLine = linesRef.current.find((line) => String(line.apInvoiceLineId) === String(lineId));
    if (!existingLine?.apInvoiceLineId) return oldRow;

    const nextDescription = newRow.lineItemDescription ?? '';
    const nextQuantity = toLineNumber(newRow.quantity);
    const nextRate = toLineNumber(newRow.lineItemRate);
    const nextTotal = toLineNumber(newRow.lineItemTotal);
    const nextProductCode = newRow.productCode || null;
    const nextBusinessUnitId = newRow.businessUnitId || null;
    let nextGlAccountId = newRow.glAccountId || null;
    if (nextGlAccountId && !filterGlAccountsForBusinessUnit(
      glAccounts,
      businessUnitOptions,
      nextBusinessUnitId
    ).some((account) => String(account.ledgerAccountId) === String(nextGlAccountId))) {
      nextGlAccountId = null;
    }

    const prevBusinessUnitId = existingLine.businessUnitId || null;
    const prevGlAccountId = existingLine.glAccountId || null;

    const unchanged =
      String(existingLine.lineItemDescription || '') === String(nextDescription) &&
      toLineNumber(existingLine.quantity) === nextQuantity &&
      toLineNumber(existingLine.lineItemRate) === nextRate &&
      toLineNumber(existingLine.lineItemTotal) === nextTotal &&
      String(existingLine.productCode || '') === String(nextProductCode || '') &&
      String(existingLine.businessUnitId || '') === String(nextBusinessUnitId || '') &&
      String(existingLine.glAccountId || '') === String(nextGlAccountId || '');

    const nextRowValues = {
      ...newRow,
      lineItemDescription: nextDescription,
      quantity: nextQuantity,
      lineItemRate: nextRate,
      lineItemTotal: nextTotal,
      productCode: nextProductCode || '',
      businessUnitId: nextBusinessUnitId || '',
      glAccountId: nextGlAccountId || '',
    };

    if (unchanged) {
      return nextRowValues;
    }

    const userId = getUserId();
    const updatedLine = {
      ...existingLine,
      lineItemDescription: nextDescription,
      quantity: nextQuantity,
      lineItemRate: nextRate,
      lineItemTotal: nextTotal,
      productCode: nextProductCode,
      businessUnitId: nextBusinessUnitId,
      glAccountId: nextGlAccountId,
      updatedBy: userId,
    };

    setLines((prev) => prev.map((line) => (
      String(line.apInvoiceLineId) === String(lineId) ? { ...line, ...updatedLine } : line
    )));

    const previousStatus = header?.workflowStatus;

    try {
      const saved = await apiFetch(`conductor/ap-invoice-lines/${lineId}`, {
        method: 'PUT',
        body: JSON.stringify(updatedLine),
      });
      const persisted = saved || updatedLine;
      if (saved) {
        setLines((prev) => prev.map((line) => (
          String(line.apInvoiceLineId) === String(lineId) ? saved : line
        )));
      }

      const existingSplits = splitsByLineId[lineId] || [];
      const buChanged = String(prevBusinessUnitId || '') !== String(persisted.businessUnitId || '');
      const glChanged = String(prevGlAccountId || '') !== String(persisted.glAccountId || '');
      if (buChanged || glChanged) {
        setMappingMemoryByLineId((prev) => {
          if (!prev[String(lineId)]) return prev;
          const next = { ...prev };
          delete next[String(lineId)];
          return next;
        });
      }
      if (existingSplits.length && (buChanged || glChanged)) {
        const nextSplits = existingSplits.map((split) => ({
          businessUnitId: buChanged && (
            !split.businessUnitId || String(split.businessUnitId) === String(prevBusinessUnitId || '')
          )
            ? persisted.businessUnitId
            : split.businessUnitId,
          glAccountId: glChanged ? persisted.glAccountId : split.glAccountId,
          splitAmount: Number(split.splitAmount),
        }));
        if (nextSplits.every((split) => split.businessUnitId && split.glAccountId && Number(split.splitAmount) > 0)) {
          const savedSplits = await apiFetch('conductor/ap-invoice-line-splits/by-ap-invoice-line', {
            method: 'PUT',
            body: JSON.stringify({
              tenantId,
              apInvoiceId: invoiceId,
              apInvoiceLineId: lineId,
              updatedBy: userId,
              splits: nextSplits,
            }),
          });
          if (Array.isArray(savedSplits)) {
            setLineSplits((prev) => [
              ...prev.filter((split) => String(split.apInvoiceLineId) !== String(lineId)),
              ...savedSplits,
            ]);
          }
        }
      }

      await refreshHeaderAfterCodingSave(previousStatus);

      return {
        ...nextRowValues,
        lineItemDescription: persisted.lineItemDescription || '',
        quantity: persisted.quantity,
        lineItemRate: persisted.lineItemRate,
        lineItemTotal: persisted.lineItemTotal,
        productCode: persisted.productCode || '',
        businessUnitId: persisted.businessUnitId || '',
        glAccountId: persisted.glAccountId || '',
      };
    } catch (err) {
      showError?.(err.message || 'Failed to update line item');
      fetchInvoiceDetails();
      throw err;
    }
  }, [
    canEditLineFields,
    toLineNumber,
    glAccounts,
    businessUnitOptions,
    splitsByLineId,
    tenantId,
    invoiceId,
    header?.workflowStatus,
    showError,
    fetchInvoiceDetails,
    refreshHeaderAfterCodingSave,
  ]);

  const handleLineProcessRowUpdateError = useCallback((err) => {
    showError?.(err?.message || 'Failed to update line item');
  }, [showError]);

  const processExpenseRowUpdate = useCallback(async (newRow, oldRow) => {
    const lineId = newRow.apInvoiceLineId || newRow.id;
    const existingLine = linesRef.current.find((line) => String(line.apInvoiceLineId) === String(lineId));
    if (!existingLine?.apInvoiceLineId) return oldRow;

    const result = await processLineRowUpdate({
      id: lineId,
      lineItemDescription: newRow.description ?? existingLine.lineItemDescription ?? '',
      quantity: existingLine.quantity,
      lineItemRate: existingLine.lineItemRate,
      lineItemTotal: existingLine.lineItemTotal,
      productCode: existingLine.productCode,
      businessUnitId: newRow.businessUnitId,
      glAccountId: newRow.glAccountId,
    }, {
      id: lineId,
      lineItemDescription: oldRow.description,
      businessUnitId: oldRow.businessUnitId,
      glAccountId: oldRow.glAccountId,
    });

    return {
      ...newRow,
      description: result.lineItemDescription,
      businessUnitId: result.businessUnitId,
      glAccountId: result.glAccountId,
      businessUnitName: getBusinessUnitLabel(result.businessUnitId, newRow.businessUnitName),
      glAccount: getGlAccountLabel(result.glAccountId),
    };
  }, [processLineRowUpdate, getBusinessUnitLabel, getGlAccountLabel]);

  const renderBusinessUnitEditCell = useCallback((params) => {
    const selected = businessUnitOptions.find((bu) => String(bu.businessUnitId) === String(params.value)) || null;
    return (
      <Autocomplete
        fullWidth
        size="small"
        options={businessUnitOptions}
        getOptionLabel={(option) => option.businessUnitName || option.label || ''}
        filterOptions={filterBusinessUnitOptions}
        value={selected}
        onChange={async (_, newValue) => {
          const nextId = newValue ? newValue.businessUnitId : null;
          await params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: nextId,
          });
          params.api.stopCellEditMode({ id: params.id, field: params.field });
        }}
        isOptionEqualToValue={(option, value) => String(option.businessUnitId) === String(value?.businessUnitId)}
        ListboxProps={{ style: { maxHeight: 280 } }}
        sx={{ px: 0.5, fontSize: '0.8125rem' }}
        renderInput={(inputParams) => (
          <TextField
            {...inputParams}
            placeholder="Search business unit"
            variant="standard"
            InputProps={{
              ...inputParams.InputProps,
              disableUnderline: true,
            }}
          />
        )}
      />
    );
  }, [businessUnitOptions]);

  const renderLearnedGlCell = useCallback((glAccountId, lineId) => {
    const learned = mappingMemoryByLineId[String(lineId)];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, width: '100%' }}>
        <Typography variant="body2" noWrap sx={{ fontSize: '0.8125rem', flex: 1 }}>
          {getGlAccountLabel(glAccountId)}
        </Typography>
        {learned?.applied ? (
          <Tooltip title="Auto-filled from a prior invoice for this vendor">
            <Chip label="Learned" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
          </Tooltip>
        ) : null}
      </Box>
    );
  }, [mappingMemoryByLineId, getGlAccountLabel]);

  const renderGlAccountEditCell = useCallback((params) => {
    const rowBuId = params.row?.businessUnitId || header?.businessUnitId;
    const options = filterGlAccountsForBusinessUnit(
      glAccounts,
      businessUnitOptions,
      rowBuId,
      params.value
    );
    const selected = options.find((account) => String(account.ledgerAccountId) === String(params.value))
      || glAccounts.find((account) => String(account.ledgerAccountId) === String(params.value))
      || null;
    return (
      <Autocomplete
        fullWidth
        size="small"
        options={options}
        getOptionLabel={getGlAccountOptionLabel}
        filterOptions={filterGlAccountOptions}
        value={selected}
        onChange={async (_, newValue) => {
          const nextId = newValue ? newValue.ledgerAccountId : null;
          await params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: nextId,
          });
          params.api.stopCellEditMode({ id: params.id, field: params.field });
        }}
        isOptionEqualToValue={(option, value) => String(option.ledgerAccountId) === String(value?.ledgerAccountId)}
        ListboxProps={{ style: { maxHeight: 280 } }}
        sx={{ px: 0.5, fontSize: '0.8125rem' }}
        renderInput={(inputParams) => (
          <TextField
            {...inputParams}
            placeholder="Search GL account"
            variant="standard"
            InputProps={{
              ...inputParams.InputProps,
              disableUnderline: true,
            }}
          />
        )}
      />
    );
  }, [glAccounts, businessUnitOptions, header?.businessUnitId]);

  const lineColumns = useMemo(() => {
    const cols = [
      { field: 'lineItemNumber', headerName: 'Line #', width: 80 },
      {
        field: 'lineItemDescription',
        headerName: 'Description',
        flex: 1,
        minWidth: 180,
        editable: canEditLineFields,
      },
      {
        field: 'quantity',
        headerName: 'Qty',
        width: 90,
        type: 'number',
        editable: canEditLineFields,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'lineItemRate',
        headerName: 'Unit Price',
        width: 120,
        type: 'number',
        editable: canEditLineFields,
        align: 'left',
        headerAlign: 'left',
        valueFormatter: (p) => (p.value != null ? formatAmount(p.value, config.AmountFormat) : ''),
      },
      {
        field: 'lineItemTotal',
        headerName: 'Line Total',
        width: 120,
        type: 'number',
        editable: canEditLineFields,
        align: 'left',
        headerAlign: 'left',
        valueFormatter: (p) => (p.value != null ? formatAmount(p.value, config.AmountFormat) : ''),
      },
      {
        field: 'productCode',
        headerName: 'Part/SKU',
        width: 120,
        editable: canEditLineFields,
      },
    ];

    if (!isDraftPreview) {
      cols.unshift(buildLineSelectColumn({
        selectedIds: selectedLineIds,
        allIds: allLineIds,
        disabled: !canEditLineFields,
        onToggle: toggleLineSelection,
        onToggleAll: setSelectedLineIds,
      }));
      cols.push({
        field: 'businessUnitId',
        headerName: 'Business Unit',
        minWidth: 180,
        flex: 0.8,
        sortable: false,
        editable: canEditLineFields,
        renderCell: (params) => (
          <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
            {getBusinessUnitLabel(params.value)}
          </Typography>
        ),
        renderEditCell: renderBusinessUnitEditCell,
      });
      cols.push({
        field: 'glAccountId',
        headerName: 'GL Account',
        flex: 1,
        minWidth: 220,
        sortable: false,
        editable: canEditLineFields,
        renderCell: (params) => renderLearnedGlCell(params.value, params.id),
        renderEditCell: renderGlAccountEditCell,
      });
    }

    return cols;
  }, [
    config.AmountFormat,
    isDraftPreview,
    canEditLineFields,
    getGlAccountLabel,
    getBusinessUnitLabel,
    renderBusinessUnitEditCell,
    renderGlAccountEditCell,
    renderLearnedGlCell,
    selectedLineIds,
    allLineIds,
    toggleLineSelection,
  ]);



  const expenseAmountColumn = useMemo(() => ({
    field: 'amount',
    headerName: 'Amount',
    width: 120,
    align: 'right',
    headerAlign: 'right',
    valueFormatter: (p) => formatAmount(p.value, config.AmountFormat),
  }), [config.AmountFormat]);

  const expenseReviewColumns = useMemo(() => [
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 160 },
    { field: 'businessUnitName', headerName: 'Business Unit', width: 130 },
    { field: 'glAccount', headerName: 'GL Account', width: 180 },
    { field: 'purchaseOrder', headerName: 'Purchase Order', width: 120 },
    { field: 'budgetCode', headerName: 'Budget Code', width: 110 },
    expenseAmountColumn,
  ], [expenseAmountColumn]);

  const expenseSplitColumns = useMemo(() => [
    buildLineSelectColumn({
      selectedIds: selectedSplitLineIds,
      allIds: allExpenseLineIds,
      disabled: !canEditLineFields,
      onToggle: toggleSplitLineSelection,
      onToggleAll: setSelectedSplitLineIds,
    }),
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 160, editable: canEditLineFields },
    {
      field: 'businessUnitId',
      headerName: 'Business Unit',
      minWidth: 180,
      flex: 0.8,
      sortable: false,
      editable: canEditLineFields,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
          {getBusinessUnitLabel(params.value, params.row.businessUnitName)}
        </Typography>
      ),
      renderEditCell: renderBusinessUnitEditCell,
    },
    {
      field: 'glAccountId',
      headerName: 'GL Account',
      minWidth: 220,
      flex: 1,
      sortable: false,
      editable: canEditLineFields,
      renderCell: (params) => renderLearnedGlCell(params.row.glAccountId, params.row.apInvoiceLineId || params.id),
      renderEditCell: renderGlAccountEditCell,
    },
    { field: 'purchaseOrder', headerName: 'Purchase Order', width: 120 },
    { field: 'budgetCode', headerName: 'Budget Code', width: 110 },
    {
      field: 'isSplit',
      headerName: 'Split',
      width: 70,
      valueFormatter: (p) => (p.value ? 'Yes' : 'No'),
    },
    expenseAmountColumn,
    {
      field: 'actions',
      headerName: '',
      width: 48,
      sortable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <IconButton
          size="small"
          aria-label="Line actions"
          onClick={(event) => {
            event.stopPropagation();
            setLineMenuAnchor(event.currentTarget);
            setLineMenuRow(params.row);
          }}
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      ),
    },
  ], [
    expenseAmountColumn,
    selectedSplitLineIds,
    allExpenseLineIds,
    canEditLineFields,
    toggleSplitLineSelection,
    getGlAccountLabel,
    getBusinessUnitLabel,
    renderBusinessUnitEditCell,
    renderGlAccountEditCell,
    renderLearnedGlCell,
  ]);



  const lineRows = useMemo(

    () => lines.map((line, index) => ({

      id: line.apInvoiceLineId || `draft-line-${index}`,

      lineItemNumber: line.lineItemNumber != null && String(line.lineItemNumber).trim() !== ''
        ? String(line.lineItemNumber)
        : String(index + 1),

      lineItemDescription: line.lineItemDescription || '',

      quantity: line.quantity,

      lineItemRate: line.lineItemRate,

      lineItemTotal: line.lineItemTotal,

      productCode: line.productCode || '',

      businessUnitId: line.businessUnitId || header?.businessUnitId || '',

      glAccountId: line.glAccountId || '',

    })),

    [lines, header?.businessUnitId]

  );



  const applyWorkflow = async (nextStatus, successMsg, options = {}) => {
    if (!canEdit) return;
    const { requireComment = false, actionOverride, keepOpen = false, engineAction } = options;

    if (!invoiceId || !header) return;

    if (requireComment && !comment.trim()) {

      showError?.('Please enter a comment.');

      return;

    }

    if (nextStatus === 'PENDING_APPROVAL' && engineAction !== 'NEXT' && (!lines.length || hasMissingGlAccount)) {
      const reason = lines.length
        ? 'Assign a GL account to every line item before submitting for approval.'
        : 'Add line items and assign a GL account before submitting for approval.';
      showError?.(reason, 'GL Account Required');
      const needsSplitTab = lines.some((line) => {
        const splits = splitsByLineId[line.apInvoiceLineId] || [];
        return splits.length > 0 && splits.some((split) => !split.glAccountId);
      });
      setDrawerTab(needsSplitTab ? DRAWER_TAB.SPLIT : DRAWER_TAB.DETAILS);
      return;
    }

    if (nextStatus === 'APPROVED' && engineAction !== 'NEXT') {
      if (hasMissingGlAccount) {
        const reason = 'Assign a GL account before approving the invoice.';
        showError?.(reason, 'GL Account Required');
        await persistApprovalFailure(reason, ['glAccount']);
        const needsSplitTab = lines.some((line) => {
          const splits = splitsByLineId[line.apInvoiceLineId] || [];
          return splits.length > 0 && splits.some((split) => !split.glAccountId);
        });
        setDrawerTab(needsSplitTab ? DRAWER_TAB.SPLIT : DRAWER_TAB.DETAILS);
        return;
      }
      if (hasBlockingVendorValidations) {
        const vendorIssues = validations
          .filter((v) => v.id === VENDOR_NAME_VALIDATION_ID && (v.status === 'WARN' || v.status === 'FAIL'))
          .map((v) => v.label)
          .join(', ');
        const reason = `Resolve vendor name validation before approving${vendorIssues ? `: ${vendorIssues}` : ''}`;
        showError?.(reason, 'Cannot Approve');
        await persistApprovalFailure(reason, [VENDOR_NAME_VALIDATION_ID]);
        setDrawerTab(DRAWER_TAB.REVIEW);
        return;
      }
    }

    if ((nextStatus === 'APPROVED' || nextStatus === 'PENDING_APPROVAL') && engineAction !== 'NEXT' && hasBlockingValidationFailures) {
      const failedChecks = validations
        .filter((v) => v.status === 'FAIL')
        .map((v) => v.label);
      const reason = `Resolve validation failures before proceeding: ${failedChecks.join(', ')}`;
      showError?.(reason, 'Cannot Approve');
      if (nextStatus === 'APPROVED') {
        await persistApprovalFailure(reason, failedChecks);
      }
      setDrawerTab(DRAWER_TAB.REVIEW);
      return;
    }



    setConfirming(true);

    try {

      const userId = localStorage.getItem('user_id');

      const userName = getCurrentUserDisplayName();

      const useEngine =
        approvalWorkflowConfigured
        && !actionOverride
        && (nextStatus === 'PENDING_APPROVAL'
          || engineAction === 'NEXT'
          || ((nextStatus === 'APPROVED' || nextStatus === 'REJECTED')
            && approvalProgress?.approvalWorkflowProgressId));

      if (useEngine && nextStatus === 'PENDING_APPROVAL' && engineAction !== 'NEXT') {
        const progress = await apiFetch('conductor/approval-workflows/submit', {
          method: 'POST',
          body: JSON.stringify({
            tenantId,
            transactionType: 'AP_INVOICE',
            transactionId: invoiceId,
            transactionNo: header.invoiceNumber,
            submittedBy: userId || null,
            submittedByName: userName,
            comments: comment || null,
            propertyId: header.businessUnitId || null,
              context: {
              user_name: userName,
              user_role: localStorage.getItem('role_name') || localStorage.getItem('user_role') || '',
              current_screen: 'AP_INVOICE',
              business_unit: header.businessUnitId || '',
              department: header.departmentId || '',
              location: header.eventLocation || '',
              invoice_amount: header.invoiceTotalAmountDue != null ? String(header.invoiceTotalAmountDue) : '',
              vendor: header.vendorName || '',
              ledger_account: header.glCode || '',
              payment_type: header.paymentStatus || '',
              bank: header.currency || '',
              invoice_number: header.invoiceNumber || '',
              purchase_order: header.purchaseOrderNumber || '',
              // Complete document JSON (stored in context_snapshot; flat keys used for conditions)
              document: header || {},
            },
          }),
        });
        setApprovalProgress(progress?.approvalWorkflowProgressId ? progress : null);
        const refreshed = await apiFetch(`conductor/ap-invoice-headers/${invoiceId}`);
        setHeader(refreshed);
        setComment('');
        showSuccess?.(successMsg);
        onConfirmed?.();
        return;
      }

      if (useEngine && (engineAction === 'NEXT' || nextStatus === 'APPROVED' || nextStatus === 'REJECTED')
          && approvalProgress?.approvalWorkflowProgressId) {
        const progress = await apiFetch('conductor/approval-workflows/action', {
          method: 'POST',
          body: JSON.stringify({
            approvalWorkflowProgressId: approvalProgress.approvalWorkflowProgressId,
            action: engineAction === 'NEXT' ? 'NEXT' : (nextStatus === 'REJECTED' ? 'REJECT' : 'APPROVE'),
            actionBy: userId || null,
            actionByName: userName,
            comments: comment || null,
          }),
        });
        setApprovalProgress(progress?.approvalWorkflowProgressId ? progress : null);
        const refreshed = await apiFetch(`conductor/ap-invoice-headers/${invoiceId}`);
        setHeader(refreshed);
        setComment('');
        showSuccess?.(successMsg);
        onConfirmed?.();
        if (!keepOpen && (progress?.workflowStatus === 'COMPLETED' || progress?.workflowStatus === 'REJECTED' || nextStatus === 'REJECTED')) {
          onClose();
        }
        return;
      }

      // Matrix is configured but invoice was never submitted into it — block direct Approve.
      if (
        approvalWorkflowConfigured
        && !actionOverride
        && nextStatus === 'APPROVED'
        && !approvalProgress?.approvalWorkflowProgressId
      ) {
        showError?.('Submit the invoice for approval first so the approval matrix can start.');
        return;
      }

      const headerForAudit = getLastApprovalFailureReason(header) || nextStatus === 'APPROVED'
        ? { ...header, headerAdditionalData: buildHeaderAdditionalDataWithApprovalAttempt(header, null) }
        : header;

      const headerAdditionalData = appendWorkflowAuditEntry(headerForAudit, {

        nextStatus,

        comment,

        userId,

        userName,

        actionOverride,

      });

      const payload = {

        ...header,

        workflowStatus: nextStatus,

        updatedBy: userId || null,

        headerAdditionalData,

      };



      const updated = await apiFetch(`conductor/ap-invoice-headers/${invoiceId}`, {

        method: 'PUT',

        body: JSON.stringify(payload),

      });



      setHeader(updated);

      setComment('');

      showSuccess?.(successMsg);

      onConfirmed?.();

      if (!keepOpen && (nextStatus === 'APPROVED' || nextStatus === 'REJECTED')) {

        onClose();

      }

    } catch (err) {

      const message = err.message || 'Failed to update invoice workflow';
      showError?.(message);
      if (nextStatus === 'APPROVED') {
        await persistApprovalFailure(message);
      }

    } finally {

      setConfirming(false);

    }

  };



  const displayHeader = header || {};

  const workflowStatus = (displayHeader.workflowStatus || 'DRAFT').toUpperCase();

  const submitForApprovalBlockReason = workflowStatus === 'DRAFT' && (!lines.length || hasMissingGlAccount)
    ? (lines.length
      ? 'Assign a GL account to every line item before submitting for approval.'
      : 'Add line items and assign a GL account before submitting for approval.')
    : '';

  const revertBlockReason = useMemo(() => {
    if (checkingPaymentBatch) return 'Checking payment batch status...';
    if (hasPaymentBatch) return 'Cannot revert: this invoice is included in a payment batch.';
    if (approvalProgress?.approvalWorkflowProgressId) {
      if (workflowStatus === 'DRAFT' && !(approvalProgress.steps || []).length) {
        return 'No workflow step to revert to.';
      }
      return '';
    }
    if (workflowStatus !== 'APPROVED') return 'Only approved invoices can be reverted.';
    return '';
  }, [workflowStatus, checkingPaymentBatch, hasPaymentBatch, approvalProgress]);

  const canRevert = (workflowStatus === 'APPROVED'
    || workflowStatus === 'PENDING_APPROVAL'
    || workflowStatus === 'IN_REVIEW'
    || (approvalProgress?.workflowStatus || '').toUpperCase() === 'IN_REVIEW'
    || (approvalProgress?.workflowStatus || '').toUpperCase() === 'COMPLETED')
    && !hasPaymentBatch
    && !checkingPaymentBatch
    && Boolean(approvalProgress?.approvalWorkflowProgressId);

  const isApprovedInvoice = workflowStatus === 'APPROVED';
  const paymentStatus = (displayHeader.paymentStatus || 'UNPAID').toUpperCase();
  const canOverridePaymentStatus = Boolean(canEdit && !isDraftPreview && invoiceId);
  const paymentOverrideBlockReason = useMemo(() => {
    if (!canEdit) return 'You do not have permission to override payment status.';
    if (checkingPaymentBatch) return 'Checking payment batch status...';
    if (hasPaymentBatch) return 'Cannot override: this invoice is included in a payment batch.';
    return '';
  }, [canEdit, checkingPaymentBatch, hasPaymentBatch]);

  const openPaymentOverride = () => {
    if (!canOverridePaymentStatus || paymentOverrideBlockReason) return;
    const current = (header?.paymentStatus || 'UNPAID').toUpperCase();
    setPaymentOverrideStatus(current === 'PAID' ? 'UNPAID' : 'PAID');
    setPaymentOverrideReason('');
    setPaymentOverrideOpen(true);
  };

  const submitPaymentOverride = async () => {
    if (!invoiceId || !header || paymentOverrideSaving) return;
    if (paymentOverrideBlockReason) {
      showError?.(paymentOverrideBlockReason);
      return;
    }
    const nextStatus = String(paymentOverrideStatus || '').toUpperCase();
    const reason = paymentOverrideReason.trim();
    if (!reason) {
      showError?.('Enter a reason for this payment status override.');
      return;
    }
    if (nextStatus === paymentStatus) {
      showError?.(`Invoice is already ${formatPaymentStatusLabel(nextStatus)}.`);
      return;
    }

    setPaymentOverrideSaving(true);
    try {
      const updated = await apiFetch(`conductor/ap-invoice-headers/${invoiceId}/payment-status`, {
        method: 'PUT',
        body: JSON.stringify({
          paymentStatus: nextStatus,
          reason,
          userName: getCurrentUserDisplayName(),
          updatedBy: getUserId(),
        }),
      });
      setHeader(updated);
      setPaymentOverrideOpen(false);
      setPaymentOverrideReason('');
      showSuccess?.(`Payment status set to ${formatPaymentStatusLabel(nextStatus)}.`);
      onConfirmed?.();
    } catch (err) {
      showError?.(err.message || 'Failed to override payment status');
    } finally {
      setPaymentOverrideSaving(false);
    }
  };

  const currentEngineStep = useMemo(
    () => (approvalProgress?.steps || []).find((s) => (s.stepStatus || '').toUpperCase() === 'CURRENT'),
    [approvalProgress],
  );
  const isViewStep = (approvalProgress?.currentStepType || currentEngineStep?.stepType || '').toUpperCase() === 'VIEW';

  const handleRevertApproval = async () => {
    if (!header || confirming) return;
    if (approvalProgress?.approvalWorkflowProgressId) {
      setConfirming(true);
      try {
        const userId = localStorage.getItem('user_id');
        const userName = getCurrentUserDisplayName();
        const steps = approvalProgress.steps || [];
        const currentIdx = steps.findIndex((s) => (s.stepStatus || '').toUpperCase() === 'CURRENT');
        const targetStepSequence = currentIdx > 0
          ? steps[currentIdx - 1].stepSequence
          : (steps[0]?.stepSequence || 1);
        const progress = await apiFetch('conductor/approval-workflows/action', {
          method: 'POST',
          body: JSON.stringify({
            approvalWorkflowProgressId: approvalProgress.approvalWorkflowProgressId,
            action: 'REVERT',
            targetStepSequence,
            actionBy: userId || null,
            actionByName: userName,
            comments: comment || 'Reverted to earlier workflow step',
          }),
        });
        setApprovalProgress(progress?.approvalWorkflowProgressId ? progress : null);
        const refreshed = await apiFetch(`conductor/ap-invoice-headers/${invoiceId}`);
        setHeader(refreshed);
        setComment('');
        showSuccess?.('Workflow reverted to earlier step');
        onConfirmed?.();
      } catch (err) {
        showError?.(err.message || 'Failed to revert workflow step');
      } finally {
        setConfirming(false);
      }
      return;
    }
    if (!canRevert) return;
    const revertStatus = getRevertWorkflowStatus(header);
    applyWorkflow(revertStatus, 'Invoice approval reverted', {
      actionOverride: 'Approval Reverted',
      keepOpen: true,
    });
  };

  const postComment = async () => {
    if (!canAdd && !canEdit) return;
    if (!invoiceId || !header) return;
    if (!comment.trim()) {
      showError?.('Please enter a comment.');
      return;
    }
    setConfirming(true);
    try {
      const userId = localStorage.getItem('user_id');
      const userName = getCurrentUserDisplayName();
      if (approvalProgress?.approvalWorkflowProgressId) {
        const progress = await apiFetch('conductor/approval-workflows/action', {
          method: 'POST',
          body: JSON.stringify({
            approvalWorkflowProgressId: approvalProgress.approvalWorkflowProgressId,
            action: 'COMMENT',
            actionBy: userId || null,
            actionByName: userName,
            comments: comment.trim(),
          }),
        });
        setApprovalProgress(progress?.approvalWorkflowProgressId ? progress : null);
      }
      const headerAdditionalData = appendWorkflowAuditEntry(header, {
        nextStatus: header.workflowStatus || 'DRAFT',
        comment: comment.trim(),
        userId,
        userName,
        actionOverride: 'Comment',
      });
      const updated = await apiFetch(`conductor/ap-invoice-headers/${invoiceId}`, {
        method: 'PUT',
        body: JSON.stringify({
          ...header,
          headerAdditionalData,
          updatedBy: userId || null,
        }),
      });
      setHeader(updated);
      setComment('');
      showSuccess?.('Comment added');
      onConfirmed?.();
    } catch (err) {
      showError?.(err.message || 'Failed to add comment');
    } finally {
      setConfirming(false);
    }
  };

  const poReference = displayHeader.purchaseOrderNumber || displayHeader.poReference;

  const vendorCode = useMemo(() => {
    if (!displayHeader.vendorId) return null;
    const vendor = vendors.find((item) => String(item.vendorId) === String(displayHeader.vendorId));
    return vendor?.vendorCode || null;
  }, [vendors, displayHeader.vendorId]);

  const invoiceDetailsPanel = (
    <>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#000000' }}>
        Invoice Header
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4}><InfoField label="Vendor" value={displayHeader.vendorName} /></Grid>
        <Grid item xs={6} sm={4}><InfoField label="Vendor Code" value={vendorCode} /></Grid>
        <Grid item xs={6} sm={4}><InfoField label="Invoice Number" value={displayHeader.invoiceNumber} /></Grid>
        <Grid item xs={6} sm={4}><InfoField label="Tax ID" value={displayHeader.vendorTaxId || displayHeader.taxId} /></Grid>
        <Grid item xs={6} sm={4}>
          <InfoField
            label="Invoice Date"
            value={displayHeader.invoiceDate ? formatDateOnly(displayHeader.invoiceDate, config.DateFormat) : null}
          />
        </Grid>
        <Grid item xs={6} sm={4}>
          <InfoField
            label="Due Date"
            value={displayHeader.invoiceDueDate ? formatDateOnly(displayHeader.invoiceDueDate, config.DateFormat) : null}
          />
        </Grid>
        <Grid item xs={6} sm={4}><InfoField label="PO Reference" value={poReference} /></Grid>
        <Grid item xs={6} sm={4}><InfoField label="Currency" value={displayHeader.currency} /></Grid>
        <Grid item xs={12} sm={8}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, flexWrap: 'wrap' }}>
            <Box sx={{ flex: 1, minWidth: 160 }}>
              <InfoField label="Payment Status" value={formatPaymentStatusLabel(paymentStatus)} />
            </Box>
            {!isDraftPreview && canEdit ? (
              <Tooltip title={paymentOverrideBlockReason} disableHoverListener={!paymentOverrideBlockReason}>
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={openPaymentOverride}
                    disabled={Boolean(paymentOverrideBlockReason) || confirming}
                    sx={{ textTransform: 'none', mb: 1.5 }}
                  >
                    Override
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </Box>
        </Grid>
        {isDraftPreview && (
          <>
            <Grid item xs={6} sm={4}><InfoField label="Department" value={selectedDepartment?.departmentName} /></Grid>
            <Grid item xs={6} sm={4}><InfoField label="Department Code" value={selectedDepartment?.departmentCode} /></Grid>
          </>
        )}
        {!isDraftPreview && (
          <>
          <Grid item xs={12} sm={8}>
            <Tooltip
              title={isApprovedInvoice ? 'Department cannot be changed on approved invoices.' : ''}
              disableHoverListener={!isApprovedInvoice}
            >
              <FormControl fullWidth size="small" sx={{ minWidth: 280 }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  disabled={isApprovedInvoice}
                  options={activeDepartments}
                  getOptionLabel={getDepartmentOptionLabel}
                  filterOptions={filterDepartmentOptions}
                  value={selectedDepartment}
                  onChange={(_, newValue) => handleDepartmentChange(newValue ? newValue.departmentId : '')}
                  isOptionEqualToValue={(option, value) => String(option.departmentId) === String(value?.departmentId)}
                  ListboxProps={{ style: { maxHeight: 280 } }}
                  sx={{ fontSize: '0.8125rem' }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Department"
                      placeholder="Search department"
                      InputLabelProps={{ shrink: true }}
                    />
                  )}
                />
              </FormControl>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={4}>
            <InfoField label="Department Code" value={selectedDepartment?.departmentCode} />
          </Grid>
          </>
        )}
      </Grid>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: '#000000' }}>Financials</Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6}>
          <InfoField
            label="Subtotal"
            value={displayHeader.invoiceSubtotal != null ? formatAmount(displayHeader.invoiceSubtotal, config.AmountFormat) : null}
          />
        </Grid>
        <Grid item xs={6}>
          <InfoField
            label="Tax Amount"
            value={displayHeader.invoiceTotalTaxes != null ? formatAmount(displayHeader.invoiceTotalTaxes, config.AmountFormat) : null}
          />
        </Grid>
        <Grid item xs={6}>
          <InfoField
            label="Shipping / Freight"
            value={displayHeader.invoiceTotalFees != null ? formatAmount(displayHeader.invoiceTotalFees, config.AmountFormat) : null}
          />
        </Grid>
        <Grid item xs={6}>
          <InfoField
            label="Total Amount"
            value={displayHeader.invoiceTotalAmountDue != null
              ? formatAmount(displayHeader.invoiceTotalAmountDue, config.AmountFormat)
              : null}
          />
        </Grid>
      </Grid>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#000000' }}>Line Items</Typography>
      {appliedMappingCount > 0 ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          {appliedMappingCount} line{appliedMappingCount === 1 ? '' : 's'} coded from prior invoices for this vendor. You can still change GL or property.
        </Alert>
      ) : null}
      {!isDraftPreview && (
        <GlBatchEditBar
          disabled={!canEditLineFields}
          selectedCount={selectedLineIds.length}
          options={propertyGlAccounts}
          glAccountId={batchGlAccountId}
          onGlAccountIdChange={setBatchGlAccountId}
          onApply={applyBatchGlToDetails}
        />
      )}
      <Paper elevation={0} sx={{ minHeight: 180, border: '1px solid #e8e8e8', borderRadius: 2, mb: 2 }}>
        <DataGridPro
          rows={lineRows}
          columns={lineColumns}
          rowHeight={36}
          hideFooter
          disableRowSelectionOnClick
          editMode="cell"
          processRowUpdate={processLineRowUpdate}
          onProcessRowUpdateError={handleLineProcessRowUpdateError}
          isCellEditable={(params) => (
            canEditLineFields
            && params.field !== 'lineItemNumber'
            && params.field !== '__select'
          )}
          sx={{ border: 'none' }}
        />
      </Paper>
    </>
  );

  const sourceDocumentPanel = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#000000' }}>
          Source Document
        </Typography>
        {sourcePdfUrl && !sourcePdfLoading && !sourcePdfError && (
          <Tooltip title="View document full screen">
            <IconButton
              size="small"
              aria-label="View document full screen"
              onClick={() => setDocumentFullscreen(true)}
              sx={{ color: '#000000' }}
            >
              <FullscreenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          minHeight: 420,
          border: '1px solid #e8e8e8',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: '#f5f5f5',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {sourcePdfLoading ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : sourcePdfError ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{sourcePdfError}</Alert>
          </Box>
        ) : sourcePdfUrl ? (
          <Box
            component="iframe"
            src={`${sourcePdfUrl}#toolbar=1&navpanes=0&scrollbar=1`}
            title="Invoice PDF preview"
            sx={{ flex: 1, width: '100%', border: 'none', bgcolor: '#fff' }}
          />
        ) : (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              No uploaded invoice PDF is linked to this record.
            </Typography>
          </Box>
        )}
      </Paper>
    </>
  );

  return (

    <>

    <Drawer

      anchor="right"

      open={open}

      onClose={onClose}

      PaperProps={{
        sx: {
          width: isDrawerFullscreen ? '100%' : { xs: '100%', md: '75vw' },
          maxWidth: '100%',
          height: '100%',
          transition: 'width 0.2s ease',
        },
      }}

    >

      <Box sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>

        <Box sx={harmonateDrawerHeaderSx}>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {isDraftPreview ? 'Invoice Preview (Draft)' : 'Invoice Review'}
            </Typography>
            {!loading && header && !isDraftPreview && String(header.workflowStatus || '').toUpperCase() === 'APPROVED' ? (
              <Chip
                label={trustedVendorAutoApproved ? 'Auto-approved' : 'Approved'}
                size="small"
                color="success"
                sx={{ fontWeight: 600 }}
              />
            ) : null}
            {!loading && header && !isDraftPreview && approvalFailureAlert && (
              <Tooltip title={`Approval blocked: ${approvalFailureAlert}`}>
                <WarningAmberIcon
                  aria-label={`Approval blocked: ${approvalFailureAlert}`}
                  sx={{ color: '#d32f2f', fontSize: 22 }}
                />
              </Tooltip>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={isDrawerFullscreen ? 'Exit full screen' : 'Full screen'}>
              <IconButton
                size="small"
                aria-label={isDrawerFullscreen ? 'Exit full screen' : 'Full screen'}
                onClick={() => setIsDrawerFullscreen((prev) => !prev)}
                sx={harmonateDialogCloseIconSx}
              >
                {isDrawerFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
            <IconButton onClick={onClose} size="small" sx={harmonateDialogCloseIconSx}><CloseIcon /></IconButton>
          </Box>

        </Box>

       



        {!isDraftPreview && header && (

          <Tabs

            value={drawerTab}

            onChange={(_, v) => setDrawerTab(v)}

            sx={harmonateTabsSx}

          >

            <Tab label="Invoice Details" />

            <Tab label="Split" />

            <Tab label="Review & Approval" />

          </Tabs>

        )}



        <Divider sx={{ mb: 2 }} />

        {!loading && header && trustedVendorAutoApproved ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {TRUSTED_VENDOR_AUTO_APPROVE_COMMENT}. This invoice bypassed the approval workflow and is eligible for Payment Proposal.
          </Alert>
        ) : null}

        {loading && (

          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>

            <CircularProgress />

          </Box>

        )}



        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}



        {!loading && header && (isDraftPreview || drawerTab === 0) && (
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {!isDraftPreview ? (
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, minHeight: 0, height: '100%' }}>
                <Box sx={{ flex: { xs: '0 0 auto', md: '0 0 50%' }, minWidth: { md: 280 }, height: { xs: 320, md: 'auto' }, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  {sourceDocumentPanel}
                </Box>
                <Box sx={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
                  {invoiceDetailsPanel}
                </Box>
              </Box>
            ) : (
              <Box sx={{ flex: 1, overflow: 'auto' }}>
                {invoiceDetailsPanel}
              </Box>
            )}
          </Box>
        )}



        {!loading && header && !isDraftPreview && drawerTab === DRAWER_TAB.SPLIT && (
          <Box sx={{ overflow: 'auto', minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#000000' }}>
              Expense Distribution
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Double-click Business Unit or GL Account to allocate each line to a property and account. Use the row menu to split one line across multiple properties and GLs.
            </Typography>
            <GlBatchEditBar
              disabled={isApprovedInvoice}
              selectedCount={selectedSplitLineIds.length}
              options={propertyGlAccounts}
              glAccountId={batchGlAccountId}
              onGlAccountIdChange={setBatchGlAccountId}
              onApply={applyBatchGlToSplit}
            />
            <Paper elevation={0} sx={{ border: '1px solid #e8e8e8', borderRadius: 2, overflow: 'hidden' }}>
              <DataGridPro
                rows={expenseLines}
                columns={expenseSplitColumns}
                rowHeight={36}
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                editMode="cell"
                processRowUpdate={processExpenseRowUpdate}
                onProcessRowUpdateError={handleLineProcessRowUpdateError}
                isCellEditable={(params) => (
                  canEditLineFields
                  && ['description', 'businessUnitId', 'glAccountId'].includes(params.field)
                )}
                sx={{ border: 'none' }}
              />
            </Paper>
          </Box>
        )}

        {!loading && header && !isDraftPreview && drawerTab === DRAWER_TAB.REVIEW && (
          <Box sx={{ flex: 1, overflow: 'auto', minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#000000' }}>
              Expense Distribution
            </Typography>
            <Paper elevation={0} sx={{ border: '1px solid #e8e8e8', borderRadius: 2, mb: 2, overflow: 'hidden' }}>
              <DataGridPro
                rows={expenseLines}
                columns={expenseReviewColumns}
                rowHeight={36}
                autoHeight
                hideFooter
                disableRowSelectionOnClick
                sx={{ border: 'none' }}
              />
            </Paper>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>Validation</Typography>

            <Grid container spacing={1.5} sx={{ mb: 2 }}>

              {validations.map((v) => (

                <Grid item xs={12} key={v.id}>

                  <Box sx={{
                    display: 'flex',
                    gap: 1.5,
                    p: 1.5,
                    bgcolor: '#fafafa',
                    borderRadius: 1,
                    ...(v.id === 'threeWay' ? { opacity: 0.5 } : {}),
                  }}>

                    <Chip label={v.status} size="small" color={validationStatusColor(v.status)} />

                    <Box>

                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{v.label}</Typography>

                      <Typography variant="caption" color="text.secondary" display="block">{v.description}</Typography>

                      <Typography variant="body2" sx={{ mt: 0.5 }}>{v.detail}</Typography>

                    </Box>

                  </Box>

                </Grid>

              ))}

            </Grid>



            <ApprovalWorkflowPanel progress={approvalProgress} />

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Audit Trail</Typography>

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>

              Approval history, payment overrides, and workflow actions

            </Typography>

            <Paper elevation={0} sx={{ border: '1px solid #e8e8e8', borderRadius: 2, mb: 2, overflow: 'hidden' }}>

              {auditTrail.length === 0 ? (

                <Box sx={{ p: 2 }}>

                  <Typography variant="body2" color="text.secondary">No approval actions recorded yet.</Typography>

                </Box>

              ) : (

                auditTrail.map((entry, index) => (

                  <Box

                    key={entry.id || index}

                    sx={{

                      px: 2,

                      py: 1.5,

                      borderBottom: index < auditTrail.length - 1 ? '1px solid #eee' : 'none',

                    }}

                  >

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>

                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{entry.action}</Typography>

                      <Typography variant="caption" color="text.secondary">

                        {entry.timestamp ? formatDateTime(entry.timestamp, config.DateTimeFormat) : '—'}

                      </Typography>

                    </Box>

                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>

                      {getAuditActorLabel(entry, userNameMap)}

                    </Typography>

                    {entry.comment ? (

                      <Typography variant="body2" sx={{ mt: 0.75, color: 'text.secondary' }}>

                        {entry.comment}

                      </Typography>

                    ) : null}

                  </Box>

                ))

              )}

            </Paper>



            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Workflow</Typography>

            <TextField

              fullWidth

              multiline

              minRows={2}

              size="small"

              label="Comments"

              value={comment}

              onChange={(e) => setComment(e.target.value)}

              sx={{ mb: 1.5 }}

            />

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ChatBubbleOutlineIcon />}
                onClick={postComment}
                disabled={confirming || !comment.trim() || (!canAdd && !canEdit)}
                sx={{ textTransform: 'none' }}
              >
                Add comment
              </Button>
              <Tooltip title={submitForApprovalBlockReason} disableHoverListener={!submitForApprovalBlockReason}>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<SendIcon />}
                    onClick={() => applyWorkflow('PENDING_APPROVAL', 'Invoice submitted for approval')}
                    disabled={workflowStatus !== 'DRAFT' || confirming || !canEdit || Boolean(submitForApprovalBlockReason)}
                    sx={{ textTransform: 'none' }}
                  >
                    Submit for approval
                  </Button>
                </span>
              </Tooltip>

              {isViewStep && approvalProgress?.approvalWorkflowProgressId && (workflowStatus === 'PENDING_APPROVAL' || workflowStatus === 'IN_REVIEW' || approvalProgress?.workflowStatus === 'IN_REVIEW' || approvalProgress?.workflowStatus === 'PENDING') ? (
                <Tooltip
                  title={
                    approvalProgress?.canCurrentUserAct === false
                      ? (approvalProgress.authorizationMessage || 'Not authorized for this step')
                      : 'Review this step and click Next'
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<NavigateNextIcon />}
                      onClick={() => applyWorkflow('PENDING_APPROVAL', 'Moved to next step', {
                        engineAction: 'NEXT',
                        keepOpen: true,
                      })}
                      disabled={confirming || approvalProgress?.canCurrentUserAct === false}
                      sx={harmonateProceedButtonSx}
                    >
                      {confirming ? 'Updating...' : 'Next'}
                    </Button>
                  </span>
                </Tooltip>
              ) : (
              <Tooltip
                title={
                  approvalProgress?.approvalWorkflowProgressId && approvalProgress?.canCurrentUserAct === false
                    ? (approvalProgress.authorizationMessage || 'Not authorized for this step')
                    : (approvalWorkflowConfigured
                      && !approvalProgress?.approvalWorkflowProgressId
                      && workflowStatus === 'DRAFT'
                      ? 'Submit for approval first to start the approval matrix'
                      : approveBlockReason)
                }
                disableHoverListener={
                  !(
                    (approvalProgress?.approvalWorkflowProgressId && approvalProgress?.canCurrentUserAct === false)
                    || (approvalWorkflowConfigured
                      && !approvalProgress?.approvalWorkflowProgressId
                      && workflowStatus === 'DRAFT')
                    || approveBlockReason
                  )
                }
              >
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<CheckCircleOutlineIcon />}
                    onClick={() => applyWorkflow('APPROVED', approvalProgress?.approvalWorkflowProgressId
                      ? 'Approval step completed'
                      : 'Invoice approved successfully')}
                    disabled={
                      workflowStatus === 'APPROVED'
                      || workflowStatus === 'REJECTED'
                      || confirming
                      || !canApprove
                      || !canEdit
                      || (approvalWorkflowConfigured
                        && !approvalProgress?.approvalWorkflowProgressId
                        && (workflowStatus === 'DRAFT' || workflowStatus === 'PENDING_APPROVAL'))
                      || (approvalProgress?.approvalWorkflowProgressId && approvalProgress?.canCurrentUserAct === false)
                    }
                    sx={harmonateProceedButtonSx}
                  >
                    {confirming ? 'Updating...' : (approvalProgress?.currentStepName ? 'Approve Step' : 'Approve')}
                  </Button>
                </span>
              </Tooltip>
              )}

              {!isViewStep && approvalProgress?.approvalWorkflowProgressId && (workflowStatus === 'PENDING_APPROVAL' || workflowStatus === 'IN_REVIEW' || approvalProgress?.workflowStatus === 'IN_REVIEW' || approvalProgress?.workflowStatus === 'PENDING') && (
                <Tooltip
                  title={
                    approvalProgress?.canCurrentUserAct === false
                      ? (approvalProgress.authorizationMessage || 'Not authorized for this step')
                      : ''
                  }
                  disableHoverListener={approvalProgress?.canCurrentUserAct !== false}
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      startIcon={<CancelOutlinedIcon />}
                      onClick={() => applyWorkflow('REJECTED', 'Invoice rejected', { requireComment: true })}
                      disabled={confirming || approvalProgress?.canCurrentUserAct === false || !canEdit}
                      sx={{ textTransform: 'none' }}
                    >
                      Reject
                    </Button>
                  </span>
                </Tooltip>
              )}

              <Tooltip title={revertBlockReason} disableHoverListener={!revertBlockReason}>
                <span>
                  <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={<UndoIcon />}
                    onClick={handleRevertApproval}
                    disabled={!canRevert || confirming || !canEdit}
                    sx={{ textTransform: 'none' }}
                  >
                    Revert
                  </Button>
                </span>
              </Tooltip>

            </Box>

            {canEdit ? (
              <Box sx={{ mt: 1.5 }}>
                <Tooltip title={paymentOverrideBlockReason} disableHoverListener={!paymentOverrideBlockReason}>
                  <span>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={openPaymentOverride}
                      disabled={Boolean(paymentOverrideBlockReason) || confirming}
                      sx={{ textTransform: 'none' }}
                    >
                      Override payment status ({formatPaymentStatusLabel(paymentStatus)})
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            ) : null}

          </Box>

        )}



        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', pt: 2, mt: 'auto', borderTop: '1px solid #eee' }}>

          <Button onClick={onClose} variant="outlined" sx={harmonateCancelButtonSx}>Close</Button>

          {isDraftPreview && (

            <Button

              variant="contained"

              disabled

              sx={harmonateProceedButtonSx}

            >

              Save invoice first to review

            </Button>

          )}

        </Box>

      </Box>

    </Drawer>

    <Dialog
      fullScreen
      open={documentFullscreen && Boolean(sourcePdfUrl)}
      onClose={() => setDocumentFullscreen(false)}
      PaperProps={{ sx: { display: 'flex', flexDirection: 'column' } }}
    >
      <DialogTitle
        sx={{
          ...harmonateDialogTitleSx,
          py: 1.25,
          px: 2,
          flexShrink: 0,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'inherit' }}>
          {displayHeader.invoiceNumber
            ? `Invoice ${displayHeader.invoiceNumber}`
            : 'Source Document'}
        </Typography>
        <Tooltip title="Exit full screen">
          <IconButton
            size="small"
            aria-label="Exit full screen"
            onClick={() => setDocumentFullscreen(false)}
            sx={harmonateDialogCloseIconSx}
          >
            <FullscreenExitIcon />
          </IconButton>
        </Tooltip>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
        <Box
          component="iframe"
          src={`${sourcePdfUrl}#toolbar=1&navpanes=0&scrollbar=1`}
          title="Invoice PDF full screen"
          sx={{ flex: 1, width: '100%', border: 'none', bgcolor: '#fff' }}
        />
      </DialogContent>
    </Dialog>

    <Dialog
      open={paymentOverrideOpen}
      onClose={() => !paymentOverrideSaving && setPaymentOverrideOpen(false)}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>Override payment status</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use this for invoices paid outside Conductor (for example, entered in Sage) so Payment Proposal does not create a duplicate payment.
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 2, mt: 0.5 }}>
          <InputLabel id="payment-override-status-label">Payment status</InputLabel>
          <Select
            labelId="payment-override-status-label"
            label="Payment status"
            value={paymentOverrideStatus}
            onChange={(event) => setPaymentOverrideStatus(event.target.value)}
            disabled={paymentOverrideSaving}
          >
            {PAYMENT_STATUS_OPTIONS.map((option) => (
              <MenuItem
                key={option.value}
                value={option.value}
                disabled={option.value === paymentStatus}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          required
          multiline
          minRows={2}
          label="Reason"
          value={paymentOverrideReason}
          onChange={(event) => setPaymentOverrideReason(event.target.value)}
          disabled={paymentOverrideSaving}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={() => setPaymentOverrideOpen(false)}
          disabled={paymentOverrideSaving}
          variant="outlined"
          sx={harmonateCancelButtonSx}
        >
          Cancel
        </Button>
        <Button
          onClick={submitPaymentOverride}
          disabled={paymentOverrideSaving || !paymentOverrideReason.trim() || paymentOverrideStatus === paymentStatus}
          variant="contained"
          sx={harmonateProceedButtonSx}
        >
          {paymentOverrideSaving ? 'Saving...' : 'Save override'}
        </Button>
      </DialogActions>
    </Dialog>

    <InvoiceLineSplitDialog
      open={splitDialogOpen}
      onClose={() => setSplitDialogOpen(false)}
      tenantId={tenantId}
      apInvoiceId={invoiceId}
      line={splitDialogLine}
      businessUnits={businessUnitOptions}
      glAccounts={glAccounts}
      readOnly={splitDialogMode === 'view'}
      onSaved={(saved) => {
        const lineId = splitDialogLine?.apInvoiceLineId;
        const splitRows = Array.isArray(saved) ? saved : [];
        const previousStatus = header?.workflowStatus;
        setLineSplits((prev) => {
          const others = prev.filter((split) => split.apInvoiceLineId !== lineId);
          return [...others, ...splitRows];
        });
        setLines((prev) => prev.map((line) => (
          line.apInvoiceLineId === lineId
            ? { ...line, isSplit: splitRows.length > 0 }
            : line
        )));
        refreshHeaderAfterCodingSave(previousStatus);
      }}
      showError={showError}
      showSuccess={showSuccess}
    />

    <Menu anchorEl={lineMenuAnchor} open={Boolean(lineMenuAnchor)} onClose={closeLineMenu}>
      <Tooltip
        title={
          isApprovedInvoice
            ? 'Approved invoices cannot be split.'
            : (!lineMenuRow?.isSplit && !canAdd)
              ? 'No add permission'
              : (lineMenuRow?.isSplit && !canEdit)
                ? 'No edit permission'
                : ''
        }
        disableHoverListener={
          !isApprovedInvoice
          && ((lineMenuRow?.isSplit && canEdit) || (!lineMenuRow?.isSplit && canAdd))
        }
      >
        <span>
          <MenuItem
            disabled={
              isApprovedInvoice
              || (!lineMenuRow?.isSplit && !canAdd)
              || (lineMenuRow?.isSplit && !canEdit)
            }
            onClick={() => {
              openSplitDialog(lineMenuRow, 'add');
              closeLineMenu();
            }}
          >
            <ListItemIcon>
              {lineMenuRow?.isSplit ? <EditIcon fontSize="small" /> : <AddIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{lineMenuRow?.isSplit ? 'Edit split' : 'Add split'}</ListItemText>
          </MenuItem>
        </span>
      </Tooltip>
      <MenuItem
        disabled={!lineMenuRow?.isSplit}
        onClick={() => {
          openSplitDialog(lineMenuRow, 'view');
          closeLineMenu();
        }}
      >
        <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
        <ListItemText>View split</ListItemText>
      </MenuItem>
    </Menu>

    </>
  );

};



export default InvoicePreviewDrawer;


